import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert } from 'react-bootstrap';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { authenticationService } from '../../../../services';

import { useBeforeUnload } from '../../../../hooks';
import { makePost, makePut, makeGet } from '../../../../api/ajax';
import { Field, Tooltip } from '../../../../components/Form';
import { Modal, onHideHandler, renderShowConfirmationModal, Card, Checkbox, AddWindshieldRepairDecisionToReturnUrl } from '../../../../components';
import { actions as workflowsActions, constants as workflowsConstants } from '../../../../redux/workflows';
import { constants as sessionConstants } from '../../../../redux/session';

import { selectors as customerConfigurationsSelectors } from '../../../../redux/customerConfigurations';
import { selectors as countriesSelectors, actions as countriesActions } from '../../../../redux/countries';
import {
  selectors as windshieldRepairProfileSelectors,
  actions as windshieldRepairProfileActions
} from '../../../../redux/windshieldRepairabilityProfiles';

import { ReactComponent as IconCaretDown } from '../../../../assets/img/icn-caret-down.svg';
import { ReactComponent as IconCaretUp } from '../../../../assets/img/icn-caret-up.svg';
import './sass/workflow-modal.scss';

const {
  EDIT_WORKFLOW_USER_FEEDBACK_SETTINGS,
  EDIT_WINDSHIELD_REPAIR_THRESHOLDS,
  EDIT_SMART_SCAN_USER_CONSENT_SETTINGS,
  EDIT_SMART_SCAN_WINDSHIELD_REPAIR_SUMMARY_SETTINGS
} = sessionConstants.PERMISSION_TYPES;

const {
  DAMAGE_MARKING_TYPE,
  DAMAGE_MARKING_TYPE_NAME,
  DAMAGE_TYPE_SELECTION_TYPE,
  DAMAGE_TYPE_SELECTION_TYPE_NAME
} = workflowsConstants;

const handleSubmit = (formData, setSuccessMessage, setErrorMessage, dispatch, setHasUnsavedChanges, setSubmitting) => {
  const workflowId = formData.id;

  setSuccessMessage(null);
  setErrorMessage(null);
  setSubmitting(true);

  const handleError = (err) => {
    setHasUnsavedChanges(false);

    const { status, response: { error } } = err;

    switch (status) {
      case 400:
        setErrorMessage(error);
        break;
      default:
        setErrorMessage(null);
        break;
    }

    setSubmitting(false);
  };

  if (formData.expectedCountryId === '') formData.expectedCountryId = null;
  if (formData.windshieldRepairProfileId === '') formData.windshieldRepairProfileId = null;
  if (formData.publicUserReturnUrl === '') formData.publicUserReturnUrl = null;
  if (formData.reportingEmail === '') formData.reportingEmail = null;

  if (formData.windshieldRepairConfidenceThreshold === 0 || formData.windshieldRepairConfidenceThreshold === '') {
    formData.windshieldRepairConfidenceThreshold = null;
  }

  if (formData.windshieldRepairDamagePredictionConfidenceThreshold === 0 || formData.windshieldRepairDamagePredictionConfidenceThreshold === '') {
    formData.windshieldRepairDamagePredictionConfidenceThreshold = null;
  }

  if (formData.overwritableDamageType === AI_DAMAGE_OVERWRITING.OFF) {
    formData.overwritableDamageType = null;
  }

  if (formData.overwriteConfidenceThreshold === 0 || formData.overwriteConfidenceThreshold === '') {
    formData.overwriteConfidenceThreshold = null;
  }

  if (workflowId) {
    // Edit workflow
    makePut(`workflows/${workflowId}`, formData).subscribe(
      ({ response }) => {
        dispatch(workflowsActions.updateWorkflowSuccess(workflowId, response));

        setHasUnsavedChanges(false);
        setSuccessMessage('Workflow changes saved!');
        setSubmitting(false);
      },
      (err) => handleError(err)
    );
  } else {
    // Add workflow
    makePost('workflows', formData).subscribe(
      ({ response }) => {
        dispatch(workflowsActions.createWorkflowSuccess(response));

        setHasUnsavedChanges(false);
        setSuccessMessage('New workflow added!');
        setSubmitting(false);
      },
      (err) => handleError(err)
    );
  }
};

/* Repair decision type */
const REPAIR_DECISION_TYPE = {
  NONE: 0,
  WINDSHIELD: 1
};

const REPAIR_DECISION_TYPE_NAME = {
  [REPAIR_DECISION_TYPE.WINDSHIELD]: 'Windshield'
};

/* Location tracking */
const GEOLOCATION_LOCATION_TRACKING = {
  NONE: 0,
  HIDDEN: 1,
  BLOCKER: 2
};

const GEOLOCATION_LOCATION_TRACKING_NAME = {
  [GEOLOCATION_LOCATION_TRACKING.NONE]: 'None',
  [GEOLOCATION_LOCATION_TRACKING.HIDDEN]: 'Optional',
  [GEOLOCATION_LOCATION_TRACKING.BLOCKER]: 'Mandatory'
};

const AI_DAMAGE_OVERWRITING = {
  CHIP_TO_CRACK: 0,
  CRACK_TO_CHIP: 1
};

const WORKFLOW_PRESET = {
  PRE_INSPECTION_NO_DAMAGE_DETECTION: 0,
  PRE_INSPECTION_AUTOMATED_DAMAGE_DETECTION: 1,
  WINDSHIELD_CLAIMS: 2,
  RENTAL_CAR_PARKS: 3,
  CUSTOM: 4
};

const WORKFLOW_PRESET_NAME = {
  [WORKFLOW_PRESET.PRE_INSPECTION_NO_DAMAGE_DETECTION]: 'Pre-inspection (No damage detection)',
  [WORKFLOW_PRESET.PRE_INSPECTION_AUTOMATED_DAMAGE_DETECTION]: 'Pre-inspection (Automated damage detection)',
  [WORKFLOW_PRESET.WINDSHIELD_CLAIMS]: 'Windshield claims',
  [WORKFLOW_PRESET.RENTAL_CAR_PARKS]: 'Rental car parks',
  [WORKFLOW_PRESET.CUSTOM]: 'Custom',
};

const WorkflowModal = ({ show, onHide, workflow, isSavingDisabled }) => {
  const [asset, setAsset] = useState(workflowsConstants.ASSET.VEHICLE);

  const [repairDecisionType, setRepairDecisionType] = useState(REPAIR_DECISION_TYPE.NONE);
  const [geolocationLocationTracking, setGeolocationLocationTracking] = useState(GEOLOCATION_LOCATION_TRACKING.NONE);

  const [workflowPreset, setWorkflowPreset] = useState(WORKFLOW_PRESET.CUSTOM);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const { hasPermission } = authenticationService;

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const countriesFromPagedResult = useSelector(countriesSelectors.selectPagedResult)?.entities;
  const countries = countriesFromPagedResult !== undefined
    ? Object.entries(countriesFromPagedResult).map((key) => key[1])
    : [];

  const windshieldRepairProfiles = useSelector(windshieldRepairProfileSelectors.selectWindshieldRepairabilityProfiles);

  const displayVehicleTypes = !workflow || (workflow && !workflow.id);
  const editingWorkflow = workflow && workflow.id;

  useBeforeUnload(hasUnsavedChanges);

  const dispatch = useDispatch();
  const customerConfiguration = useSelector(customerConfigurationsSelectors.selectCustomerConfigurationSimple);

  /* Setting default values that does not exist explicitly in workflow settings */
  const getDefaultRepairDecisionType = useCallback(() => {
    if (workflow.windshieldRepairReplaceDecisionEnabled) return REPAIR_DECISION_TYPE.WINDSHIELD;
    return REPAIR_DECISION_TYPE.NONE;
  }, [workflow]);

  const getDefaultGeolocationLocationTracking = useCallback(() => {
    if (workflow.trackGeolocationEnabled && !workflow.trackGeolocationRequired) return GEOLOCATION_LOCATION_TRACKING.HIDDEN;
    if (workflow.trackGeolocationRequired) return GEOLOCATION_LOCATION_TRACKING.BLOCKER;
    return GEOLOCATION_LOCATION_TRACKING.NONE;
  }, [workflow]);

  const getWorkflowPreset = (workflow) => {
    const {
      damageMarkingType,
      windshieldRepairReplaceDecisionEnabled,
      damageTypeSelectionType,
      customDamagesEnabled,
      aiLocationAssistanceEnabled,
      windshieldRepairConfidenceThreshold
    } = workflow;

    if (damageMarkingType === DAMAGE_MARKING_TYPE.AI) {
      return WORKFLOW_PRESET.PRE_INSPECTION_AUTOMATED_DAMAGE_DETECTION;
    }

    if (damageMarkingType === DAMAGE_MARKING_TYPE.OFF) {
      return WORKFLOW_PRESET.PRE_INSPECTION_NO_DAMAGE_DETECTION;
    }

    if (damageMarkingType === DAMAGE_MARKING_TYPE.MANUAL
        && damageTypeSelectionType === DAMAGE_TYPE_SELECTION_TYPE.MANUAL
        && !windshieldRepairReplaceDecisionEnabled
        && !customDamagesEnabled
        && !aiLocationAssistanceEnabled
        && windshieldRepairConfidenceThreshold === 0
    ) {
      return WORKFLOW_PRESET.RENTAL_CAR_PARKS;
    }

    if (damageMarkingType === DAMAGE_MARKING_TYPE.MANUAL
        && damageTypeSelectionType === DAMAGE_TYPE_SELECTION_TYPE.MANUAL
        && windshieldRepairReplaceDecisionEnabled
        && !customDamagesEnabled
        && !aiLocationAssistanceEnabled
        && (windshieldRepairConfidenceThreshold === null || windshieldRepairConfidenceThreshold === 0)
    ) {
      return WORKFLOW_PRESET.WINDSHIELD_CLAIMS;
    }

    return WORKFLOW_PRESET.CUSTOM;
  };

  // Preset filling
  useEffect(() => {
    if (!editingWorkflow) return;
    setWorkflowPreset(getWorkflowPreset(workflow));
  }, [workflow, editingWorkflow]);

  useEffect(() => {
    if (!workflow) return;
    setRepairDecisionType(getDefaultRepairDecisionType());
    setGeolocationLocationTracking(getDefaultGeolocationLocationTracking());
  }, [workflow, getDefaultRepairDecisionType, getDefaultGeolocationLocationTracking]);
  /* End of setting default values that does not exist explicitly in workflow settings */

  useEffect(() => {
    if (customerConfiguration?.expectedCountryValidationEnabled && show && countries.find(((country) => country.name === 'Estonia')) === undefined) {
      makeGet('countries?countryName=Estonia').subscribe(
        ({ response }) => dispatch(countriesActions.loadCountriesSuccess(response))
      );
    }
  }, [customerConfiguration, show, countries, dispatch]);

  useEffect(() => {
    if (!windshieldRepairProfiles) {
      makeGet('windshieldRepairProfiles?pageSize=150').subscribe(
        ({ response }) => dispatch(windshieldRepairProfileActions.loadWindshieldRepairabilityProfilesSuccess(response))
      );
    }
  }, [windshieldRepairProfiles, dispatch]);

  const hideModal = () => {
    setRepairDecisionType(REPAIR_DECISION_TYPE.NONE);
    setGeolocationLocationTracking(GEOLOCATION_LOCATION_TRACKING.NONE);
    setSuccessMessage(null);
    setErrorMessage(null);
    setWorkflowPreset(WORKFLOW_PRESET.CUSTOM);
    setShowAdvancedSettings(false);
    onHide();
  };

  const initialValues = {
    ...workflow,
    timeLimitValidationEnabled: editingWorkflow
      ? workflow.timeLimitValidationEnabled
      : true,
    smartScanImageDownloadEnabled: editingWorkflow
      ? workflow.smartScanImageDownloadEnabled
      : true,
    windshieldRepairReplaceDecisionEnabled: editingWorkflow
      ? workflow.windshieldRepairReplaceDecisionEnabled
      : false,
    userFeedbackSettings: {
      satisfactionRatingEnabled: editingWorkflow
        ? workflow.userFeedbackSettings.satisfactionRatingEnabled
        : true,
      commentEnabled: editingWorkflow
        ? workflow.userFeedbackSettings.commentEnabled
        : true,
    },
    reportingEmail: workflow?.reportingEmail || '',
    publicUserReturnUrl: workflow?.publicUserReturnUrl || '',
    expectedCountryId: workflow?.expectedCountryId || '',
    windshieldRepairProfileId: workflow?.windshieldRepairProfileId || '',
    vehicleType: editingWorkflow
      ? workflow.vehicleType
      : workflowsConstants.VEHICLE_TYPES.CAR,
    windshieldRepairConfidenceThreshold: workflow?.windshieldRepairConfidenceThreshold || 0,
    // This value is set through additional image type settings
    windshieldRepairDamagePredictionConfidenceThreshold: workflow?.windshieldRepairDamagePredictionConfidenceThreshold || 0,
    smartScanUserConsentEnabled: editingWorkflow
      ? workflow.smartScanUserConsentEnabled
      : true,
    addWindshieldRepairDecisionToReturnUrl: workflow?.addWindshieldRepairDecisionToReturnUrl || false,
    smartScanWindshieldRepairSummaryEnabled: editingWorkflow
      ? workflow.smartScanWindshieldRepairSummaryEnabled
      : true,
    overwritableDamageSettings: workflow?.overwritableDamageSettings || [],
    damageMarkingType: editingWorkflow ? workflow.damageMarkingType : DAMAGE_MARKING_TYPE.OFF,
    customDamagesEnabled: editingWorkflow ? workflow.customDamagesEnabled : false,
    damageTypeSelectionType: editingWorkflow ? workflow.damageTypeSelectionType : DAMAGE_TYPE_SELECTION_TYPE.OFF,
    aiLocationAssistanceEnabled: editingWorkflow ? workflow.aiLocationAssistanceEnabled : false
  };

  return (
    <>
      {renderShowConfirmationModal(showConfirmModal, setShowConfirmModal, () => {
        hideModal();
        setHasUnsavedChanges(false);
        setShowConfirmModal(false);
      })}

      <Modal
        show={show}
        size="lg"
        backdrop="static"
        headerText="Workflow settings"
        onHide={() => {
          onHideHandler(hasUnsavedChanges, hideModal, setShowConfirmModal);
        }}
      >
        <Modal.Body className="create-edit-workflow-modal">
          <div className="row">
            <div className="col">
              {successMessage && (
                <Alert variant="success" dismissible onClick={() => setSuccessMessage(null)}>
                  {successMessage}
                </Alert>
              )}
              {
                errorMessage && (
                  <Alert variant="danger" dismissible onClick={() => setErrorMessage(null)}>
                    {errorMessage}
                  </Alert>
                )
              }
            </div>
          </div>
          <Formik
            initialValues={initialValues}
            validationSchema={Yup.object().shape({
              name: Yup.string().required('Workflow name is required'),
              reportingEmail: Yup.string().email('Please enter a correct email').nullable(),
              publicUserReturnUrl: Yup.string().url('Please enter a correct url').nullable(),
              windshieldRepairConfidenceThreshold: Yup
                .number()
                .min(0, 'The repairability threshold must be at least 0.')
                .max(100, 'The repairability threshold cannot exceed 100.')
            })}
            onSubmit={(formData, { setSubmitting }) => {
              handleSubmit(
                { ...formData },
                setSuccessMessage, setErrorMessage, dispatch, setHasUnsavedChanges, setSubmitting
              );
            }}
            validateOnChange={false}
          >
            {({ values, errors, touched, setFieldValue, isSubmitting }) => (
              <Form
                onChange={({ target }) => {
                  if (!hasUnsavedChanges && target.value !== initialValues[target.name]) {
                    setHasUnsavedChanges(true);
                  }
                }}
              >
                <div className="row">
                  <div className="col-sm-12">
                    <Card>
                      <Card.Header>General settings</Card.Header>
                      <Card.Body>
                        <div className="row">
                          <div className="col-sm-4">
                            <Field
                              name="name"
                              label="Workflow name"
                              placeholder="New workflow name"
                              type="text"
                              touched={touched.name}
                              errors={errors.name}
                            />
                          </div>
                          <div className="col-sm-4">
                            <Field
                              name="reportingEmail"
                              label="Report e-mail"
                              placeholder="Add report e-mail"
                              type="text"
                              touched={touched.reportingEmail}
                              errors={errors.reportingEmail}
                            />
                          </div>
                          <div className="col-sm-4">
                            <Field
                              name="publicUserReturnUrl"
                              label="Redirect URL"
                              placeholder="Add redirect URL"
                              type="text"
                              touched={touched.publicUserReturnUrl}
                              errors={errors.publicUserReturnUrl}
                            />
                            <AddWindshieldRepairDecisionToReturnUrl
                              addWindshieldRepairDecisionToReturnUrl={values.addWindshieldRepairDecisionToReturnUrl}
                              setAddWindshieldRepairDecisionToReturnUrl={() => {
                                setFieldValue('addWindshieldRepairDecisionToReturnUrl', !values.addWindshieldRepairDecisionToReturnUrl);
                              }}
                            />
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </div>
                </div>
                {
                  displayVehicleTypes && (
                    <div className="row mt-3">
                      <div className="col-sm-12">
                        <Card>
                          <Card.Header>The asset</Card.Header>
                          <Card.Body>
                            <div className="row">
                              <div className="col-sm-4">
                                <div className="form-group">
                                  <label htmlFor="asset" className="mb-0">
                                    Asset
                                  </label>
                                  <select
                                    className="form-control"
                                    value={asset}
                                    onChange={(e) => setAsset(Number(e.target.value))}
                                    name="asset"
                                    id="asset"
                                  >
                                    <option value={workflowsConstants.ASSET.VEHICLE}>
                                      {workflowsConstants.ASSET_NAME[workflowsConstants.ASSET.VEHICLE]}
                                    </option>
                                  </select>
                                </div>
                              </div>
                              <div className="col-sm-4">
                                <div className="form-group">
                                  <label htmlFor="assetType" className="mb-0">
                                    Type
                                  </label>
                                  <select
                                    className="form-control"
                                    value={values.vehicleType}
                                    onChange={(e) => setFieldValue('vehicleType', Number(e.target.value))}
                                    name="vehicleType"
                                    id="vehicleType"
                                  >
                                    {
                                      Object.keys(workflowsConstants.VEHICLE_TYPES).map((key) => (
                                        <option key={workflowsConstants.VEHICLE_TYPES[key]} value={workflowsConstants.VEHICLE_TYPES[key]}>
                                          {workflowsConstants.VEHICLE_TYPE_NAME[workflowsConstants.VEHICLE_TYPES[key]]}
                                        </option>
                                      ))
                                    }
                                  </select>
                                </div>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </div>
                    </div>
                  )
                }
                <div className="row mt-3">
                  <div className="col-sm-12">
                    <Card>
                      <Card.Header>Features</Card.Header>
                      <Card.Body>
                        <div className="row mt-2">
                          <div className="col-sm-12 sub-section">
                            <span className="sub-section-header">USAGE PURPOSE</span>
                          </div>
                        </div>
                        <div className="row mt-2">
                          <div className="col-sm-4">
                            <div className="form-group">
                              <label htmlFor="repairDecisionType" className="mb-0">
                                Workflow preset
                                <Tooltip>
                                  A preset for a certain business case. This will pre-configure advanced settings.
                                </Tooltip>
                              </label>
                              <select
                                className="form-control"
                                value={workflowPreset}
                                onChange={(e) => {
                                  const value = Number(e.target.value);

                                  if (value !== WORKFLOW_PRESET.CUSTOM) {
                                    // Turn everything off
                                    setRepairDecisionType(REPAIR_DECISION_TYPE.NONE);
                                    setFieldValue('damageMarkingType', DAMAGE_MARKING_TYPE.OFF);
                                    setFieldValue('damageTypeSelectionType', DAMAGE_TYPE_SELECTION_TYPE.OFF);
                                    setFieldValue('windshieldRepairProfileId', '');
                                    setFieldValue('windshieldRepairReplaceDecisionEnabled', false);
                                    setFieldValue('windshieldRepairConfidenceThreshold', 0);
                                    setFieldValue('aiLocationAssistanceEnabled', false);
                                    setFieldValue('customDamagesEnabled', false);

                                    if (value === WORKFLOW_PRESET.PRE_INSPECTION_AUTOMATED_DAMAGE_DETECTION) {
                                      setFieldValue('damageMarkingType', DAMAGE_MARKING_TYPE.AI);
                                    } else if (value === WORKFLOW_PRESET.RENTAL_CAR_PARKS) {
                                      setFieldValue('damageMarkingType', DAMAGE_MARKING_TYPE.MANUAL);
                                      setFieldValue('damageTypeSelectionType', DAMAGE_TYPE_SELECTION_TYPE.MANUAL);
                                    } else if (value === WORKFLOW_PRESET.WINDSHIELD_CLAIMS) {
                                      setFieldValue('damageMarkingType', DAMAGE_MARKING_TYPE.MANUAL);
                                      setFieldValue('damageTypeSelectionType', DAMAGE_TYPE_SELECTION_TYPE.MANUAL);
                                      setRepairDecisionType(REPAIR_DECISION_TYPE.WINDSHIELD);
                                    }
                                  }

                                  setWorkflowPreset(value);
                                }}
                                name="workflowPreset"
                                id="workflowPreset"
                              >
                                <option value={WORKFLOW_PRESET.CUSTOM}>
                                  {WORKFLOW_PRESET_NAME[WORKFLOW_PRESET.CUSTOM]}
                                </option>
                                <option value={WORKFLOW_PRESET.PRE_INSPECTION_AUTOMATED_DAMAGE_DETECTION}>
                                  {WORKFLOW_PRESET_NAME[WORKFLOW_PRESET.PRE_INSPECTION_AUTOMATED_DAMAGE_DETECTION]}
                                </option>
                                <option value={WORKFLOW_PRESET.PRE_INSPECTION_NO_DAMAGE_DETECTION}>
                                  {WORKFLOW_PRESET_NAME[WORKFLOW_PRESET.PRE_INSPECTION_NO_DAMAGE_DETECTION]}
                                </option>
                                <option value={WORKFLOW_PRESET.RENTAL_CAR_PARKS}>
                                  {WORKFLOW_PRESET_NAME[WORKFLOW_PRESET.RENTAL_CAR_PARKS]}
                                </option>
                                <option value={WORKFLOW_PRESET.WINDSHIELD_CLAIMS}>
                                  {WORKFLOW_PRESET_NAME[WORKFLOW_PRESET.WINDSHIELD_CLAIMS]}
                                </option>
                              </select>
                            </div>
                          </div>
                          {
                            (workflowPreset === WORKFLOW_PRESET.WINDSHIELD_CLAIMS || repairDecisionType === REPAIR_DECISION_TYPE.WINDSHIELD) && (
                              <div className="col-sm-4">
                                <div className="form-group">
                                  <label htmlFor="windshieldRepairProfileId" className="mb-0">
                                    Windshield profile
                                    <Tooltip>
                                      Select the windshield profile for AI to take as a the base to predict the reparability of the damages.
                                    </Tooltip>
                                  </label>
                                  <select
                                    className="form-control"
                                    value={values.windshieldRepairProfileId}
                                    onChange={(event) => {
                                      setFieldValue('windshieldRepairProfileId', event.target.value);
                                      setFieldValue('windshieldRepairReplaceDecisionEnabled', event.target.value !== '');
                                    }}
                                    name="windshieldRepairProfileId"
                                    id="windshieldRepairProfileId"
                                    disabled={repairDecisionType !== REPAIR_DECISION_TYPE.WINDSHIELD}
                                  >
                                    <option key="defaultWindshieldRepairProfileId" value="">Not selected</option>
                                    {
                                      windshieldRepairProfiles?.results?.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)
                                    }
                                  </select>
                                </div>
                              </div>
                            )
                          }
                        </div>
                        <div className="row mt-4 mb-4">
                          <div className="col-sm-12 d-flex justify-content-center">
                            <button
                              type="button"
                              className="btn btn-link p-0 advanced-settings-toggle"
                              onClick={() => setShowAdvancedSettings((prev) => !prev)}
                            >
                              Advanced settings
                              {showAdvancedSettings
                                ? <IconCaretUp className="ml-2" width="12" height="12" fill="currentColor" style={{ verticalAlign: 'middle' }} />
                                : <IconCaretDown className="ml-2" width="12" height="12" fill="currentColor" style={{ verticalAlign: 'middle' }} />}
                            </button>
                          </div>
                        </div>
                        {showAdvancedSettings && (
                          <>
                            <div className="row mt-2">
                              <div className="col-sm-12 sub-section">
                                <span className="sub-section-header">DAMAGE DETECTION</span>
                                <Tooltip>
                                  You can configure damage related features for each workflow if your subscription includes it.
                                </Tooltip>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-sm-4">
                                <div className="form-group">
                                  <label htmlFor="damageMarkingType" className="mb-0">
                                    Damage markup
                                    <Tooltip>
                                      <b>Off</b> - no damages are detected on the image.<br /><br />
                                      <b>Manual</b> - Damages are marked by the user of the SmartScan application.<br /><br />
                                      <b>AI</b> - Damage detection AI searches damages from the images. Processing time 2 minutes per image.
                                    </Tooltip>
                                  </label>
                                  <select
                                    className="form-control"
                                    value={values.damageMarkingType}
                                    onChange={(e) => {
                                      setFieldValue('damageMarkingType', Number(e.target.value));
                                      setWorkflowPreset(getWorkflowPreset(WORKFLOW_PRESET.CUSTOM));
                                    }}
                                    name="damageMarkingType"
                                    id="damageMarkingType"
                                  >
                                    <option value={DAMAGE_MARKING_TYPE.OFF}>
                                      {DAMAGE_MARKING_TYPE_NAME[DAMAGE_MARKING_TYPE.OFF]}
                                    </option>
                                    <option value={DAMAGE_MARKING_TYPE.MANUAL}>
                                      {DAMAGE_MARKING_TYPE_NAME[DAMAGE_MARKING_TYPE.MANUAL]}
                                    </option>
                                    <option value={DAMAGE_MARKING_TYPE.AI}>
                                      {DAMAGE_MARKING_TYPE_NAME[DAMAGE_MARKING_TYPE.AI]}
                                    </option>
                                  </select>
                                </div>
                              </div>
                              <div className="col-sm-4">
                                <div className="form-group">
                                  <label htmlFor="damageTypeSelectionType" className="mb-0">
                                    Damage type selection
                                    <Tooltip>
                                      <>
                                        <b>Manual</b> - User selects the initial damage type and precise location for the chip, AI will
                                        overwrite it, if necessary.<br /><br />
                                        <b>AI</b> - Application trusts AI on the damage type selection entirely. Reduces accuracy!
                                      </>
                                    </Tooltip>
                                  </label>
                                  <select
                                    className="form-control"
                                    value={values.damageTypeSelectionType}
                                    onChange={(e) => {
                                      setFieldValue('damageTypeSelectionType', Number(e.target.value));
                                      setWorkflowPreset(WORKFLOW_PRESET.CUSTOM);
                                    }}
                                    name="damageTypeSelectionType"
                                    id="damageTypeSelectionType"
                                    disabled={values.damageMarkingType !== DAMAGE_MARKING_TYPE.MANUAL}
                                  >
                                    <option value={DAMAGE_TYPE_SELECTION_TYPE.OFF}>
                                      {DAMAGE_TYPE_SELECTION_TYPE_NAME[DAMAGE_TYPE_SELECTION_TYPE.OFF]}
                                    </option>
                                    <option value={DAMAGE_TYPE_SELECTION_TYPE.MANUAL}>
                                      {DAMAGE_TYPE_SELECTION_TYPE_NAME[DAMAGE_TYPE_SELECTION_TYPE.MANUAL]}
                                    </option>
                                    <option value={DAMAGE_TYPE_SELECTION_TYPE.AI}>
                                      {DAMAGE_TYPE_SELECTION_TYPE_NAME[DAMAGE_TYPE_SELECTION_TYPE.AI]}
                                    </option>
                                  </select>
                                </div>
                              </div>
                              <div className="col-sm-4">
                                <div className="form-group">
                                  <label htmlFor="aiLocationAssistanceEnabled" className="mb-0">
                                    AI Damage location assistance
                                    <Tooltip>
                                      Indicates how damaged areas are found (either by AI, or user marks the area of damage).
                                    </Tooltip>
                                  </label>
                                  <select
                                    className="form-control"
                                    value={String(values.aiLocationAssistanceEnabled)}
                                    onChange={(e) => {
                                      setFieldValue('aiLocationAssistanceEnabled', e.target.value === 'true');
                                      setWorkflowPreset(WORKFLOW_PRESET.CUSTOM);
                                    }}
                                    name="aiLocationAssistanceEnabled"
                                    id="aiLocationAssistanceEnabled"
                                    disabled={values.damageMarkingType !== DAMAGE_MARKING_TYPE.MANUAL}
                                  >
                                    <option value="true">On</option>
                                    <option value="false">Off</option>
                                  </select>
                                </div>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-sm-4">
                                <div className="form-group">
                                  <label htmlFor="customDamagesEnabled" className="mb-0">
                                    Use custom damages
                                    <Tooltip>
                                      By enabling this, you will be using your custom damage types. This disables advanced features.
                                    </Tooltip>
                                  </label>
                                  <select
                                    className="form-control"
                                    value={String(values.customDamagesEnabled)}
                                    onChange={(e) => {
                                      const enabled = e.target.value === 'true';
                                      setFieldValue('customDamagesEnabled', enabled);
                                      if (enabled) {
                                        setRepairDecisionType(REPAIR_DECISION_TYPE.NONE);
                                        setFieldValue('windshieldRepairProfileId', '');
                                        setFieldValue('windshieldRepairReplaceDecisionEnabled', false);
                                        setFieldValue('windshieldRepairConfidenceThreshold', 0);
                                      }
                                      setWorkflowPreset(WORKFLOW_PRESET.CUSTOM);
                                    }}
                                    name="customDamagesEnabled"
                                    id="customDamagesEnabled"
                                    disabled={
                                      !(values.damageMarkingType === DAMAGE_MARKING_TYPE.MANUAL
                                        && values.damageTypeSelectionType === DAMAGE_TYPE_SELECTION_TYPE.MANUAL)
                                    }
                                  >
                                    <option value="true">On</option>
                                    <option value="false">Off</option>
                                  </select>
                                </div>
                              </div>
                              <div className="col-sm-4">
                                <div className="form-group">
                                  <label htmlFor="repairDecisionType" className="mb-0">
                                    Repair decision
                                    <Tooltip>
                                      Select a vehicle part you want to have DriveX repair AI to analyze the reparability of.
                                    </Tooltip>
                                  </label>
                                  <select
                                    className="form-control"
                                    value={repairDecisionType}
                                    onChange={(e) => {
                                      setRepairDecisionType(Number(e.target.value));
                                      setFieldValue('windshieldRepairProfileId', '');
                                      setFieldValue('windshieldRepairReplaceDecisionEnabled', false);
                                      setWorkflowPreset(WORKFLOW_PRESET.CUSTOM);
                                    }}
                                    name="repairDecisionType"
                                    id="repairDecisionType"
                                    disabled={values.damageMarkingType !== DAMAGE_MARKING_TYPE.MANUAL || values.customDamagesEnabled}
                                  >
                                    <option value={REPAIR_DECISION_TYPE.NONE}>
                                      Off
                                    </option>
                                    <option value={REPAIR_DECISION_TYPE.WINDSHIELD}>
                                      {REPAIR_DECISION_TYPE_NAME[REPAIR_DECISION_TYPE.WINDSHIELD]}
                                    </option>
                                  </select>
                                </div>
                              </div>
                              <div className="col-sm-4">
                                <div className="form-group">
                                  <label htmlFor="windshieldRepairProfileId" className="mb-0">
                                    Windshield profile
                                    <Tooltip>
                                      Select the windshield profile for AI to take as a the base to predict the reparability of the damages.
                                    </Tooltip>
                                  </label>
                                  <select
                                    className="form-control"
                                    value={values.windshieldRepairProfileId}
                                    onChange={(event) => {
                                      const windshieldRepairReplaceDecisionEnabled = event.target.value !== '';
                                      setFieldValue('windshieldRepairProfileId', event.target.value);
                                      setFieldValue('windshieldRepairReplaceDecisionEnabled', windshieldRepairReplaceDecisionEnabled);
                                      setWorkflowPreset(WORKFLOW_PRESET.CUSTOM);
                                    }}
                                    name="windshieldRepairProfileId"
                                    id="windshieldRepairProfileId"
                                    disabled={repairDecisionType !== REPAIR_DECISION_TYPE.WINDSHIELD || values.customDamagesEnabled}
                                  >
                                    <option key="defaultWindshieldRepairProfileId" value="">Not selected</option>
                                    {
                                      windshieldRepairProfiles?.results?.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)
                                    }
                                  </select>
                                </div>
                              </div>
                            </div>
                            {
                              hasPermission(EDIT_WINDSHIELD_REPAIR_THRESHOLDS) && (
                                <div className="row">
                                  <div className="col-sm-4">
                                    <Field
                                      type="number"
                                      min="0"
                                      max="100"
                                      name="windshieldRepairConfidenceThreshold"
                                      label="Repairability threshold"
                                      touched={touched.windshieldRepairConfidenceThreshold}
                                      errors={errors.windshieldRepairConfidenceThreshold}
                                      disabled={repairDecisionType !== REPAIR_DECISION_TYPE.WINDSHIELD || values.customDamagesEnabled}
                                      // eslint-disable-next-line max-len
                                      tooltip="Enter value between 0-100. If repair decision confidence below value then 'Repair confidence below threshold' verification error will be added."
                                    />
                                  </div>
                                </div>)
                            }
                            <div className="row">
                              <div className="col-sm-12">
                                <hr />
                              </div>
                            </div>
                          </>
                        )}
                        <div className="row">
                          <div className="col-sm-12 sub-section">
                            <span className="sub-section-header">GEOLOCATION</span>
                            <Tooltip>
                              SmartScan enables you to save the location of the user when they capture pictures.
                            </Tooltip>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-sm-4">
                            <div className="form-group">
                              <label htmlFor="geolocationLocationTracking" className="mb-0">
                                Location tracking
                              </label>
                              <select
                                className="form-control"
                                value={geolocationLocationTracking}
                                onChange={(e) => {
                                  setGeolocationLocationTracking(Number(e.target.value));

                                  setFieldValue(
                                    'trackGeolocationEnabled',
                                    Number(e.target.value) === GEOLOCATION_LOCATION_TRACKING.HIDDEN
                                    || Number(e.target.value) === GEOLOCATION_LOCATION_TRACKING.BLOCKER
                                  );
                                  setFieldValue('trackGeolocationRequired', Number(e.target.value) === GEOLOCATION_LOCATION_TRACKING.BLOCKER);

                                  // Reset other fields
                                  if (Number(e.target.value) === GEOLOCATION_LOCATION_TRACKING.NONE
                                      || Number(e.target.value) === GEOLOCATION_LOCATION_TRACKING.HIDDEN) {
                                    setFieldValue('expectedCountryId', '');
                                    setFieldValue('expectedCountryGeolocationRequired', false);
                                  }
                                }}
                                name="geolocationLocationTracking"
                                id="geolocationLocationTracking"
                              >
                                <option value={GEOLOCATION_LOCATION_TRACKING.NONE}>
                                  Off
                                </option>
                                <option value={GEOLOCATION_LOCATION_TRACKING.HIDDEN}>
                                  {GEOLOCATION_LOCATION_TRACKING_NAME[GEOLOCATION_LOCATION_TRACKING.HIDDEN]}
                                </option>
                                <option value={GEOLOCATION_LOCATION_TRACKING.BLOCKER}>
                                  {GEOLOCATION_LOCATION_TRACKING_NAME[GEOLOCATION_LOCATION_TRACKING.BLOCKER]}
                                </option>
                              </select>
                            </div>
                          </div>
                          {
                            customerConfiguration?.expectedCountryValidationEnabled && (
                              <>
                                <div className="col-sm-4">
                                  <div className="form-group">
                                    <label htmlFor="expectedCountryId" className="mb-0">
                                      Service available in
                                    </label>
                                    <select
                                      className="form-control"
                                      value={values.expectedCountryId}
                                      onChange={(event) => {
                                        setFieldValue('expectedCountryId', event.target.value);
                                        setFieldValue('expectedCountryGeolocationRequired', event.target.value !== '');
                                      }}
                                      name="expectedCountryId"
                                      id="expectedCountryId"
                                      disabled={geolocationLocationTracking !== GEOLOCATION_LOCATION_TRACKING.BLOCKER}
                                    >
                                      <option key="defaultExpectedCountryId" value="">All countries</option>
                                      {
                                        countries.map((country) => <option key={country.id} value={country.id}>{country.name}</option>)
                                      }
                                    </select>
                                  </div>
                                </div>
                              </>
                            )
                          }
                        </div>
                        <div className="row">
                          <div className="col-sm-12">
                            <hr />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-sm-12 sub-section">
                            <span className="sub-section-header">CAPTURING TIME</span>
                            <Tooltip>
                              We can track how much time did user take between the images. Those features help you
                              detect suspicious activity of the user.
                            </Tooltip>
                          </div>
                        </div>
                        <div className="row mt-2">
                          <div className="col-sm-4">
                            <Checkbox
                              id="timeLimitValidationEnabled"
                              label="Time limit"
                              name="timeLimitValidationEnabled"
                              checked={values.timeLimitValidationEnabled}
                              onClickHandler={() => {
                                setFieldValue('timeLimitValidationEnabled', !values.timeLimitValidationEnabled);
                              }}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-sm-12">
                            <hr />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-sm-12 sub-section">
                            <span className="sub-section-header">SMARTSCAN SETTINGS</span>
                            <Tooltip>
                              Those settings change the way how users interact with photo capturing application in the end of the process.
                            </Tooltip>
                          </div>
                        </div>
                        <div className="row mt-2">
                          <div className="col-sm-4">
                            <Checkbox
                              id="smartScanImageDownloadEnabled"
                              label="Image download"
                              name="smartScanImageDownloadEnabled"
                              checked={values.smartScanImageDownloadEnabled}
                              onClickHandler={() => {
                                setFieldValue('smartScanImageDownloadEnabled', !values.smartScanImageDownloadEnabled);
                              }}
                            />
                          </div>
                          {
                            hasPermission(EDIT_WORKFLOW_USER_FEEDBACK_SETTINGS) && (
                              <>
                                <div className="col-sm-4 checkbox">
                                  <Checkbox
                                    id="userFeedbackSettings.satisfactionRatingEnabled"
                                    label="Customer rating"
                                    name="userFeedbackSettings.satisfactionRatingEnabled"
                                    checked={values.userFeedbackSettings.satisfactionRatingEnabled}
                                    onClickHandler={() => {
                                      setFieldValue(
                                        'userFeedbackSettings.satisfactionRatingEnabled',
                                        !values.userFeedbackSettings.satisfactionRatingEnabled
                                      );
                                    }}
                                  />
                                </div>
                                <div className="col-sm-4 checkbox">
                                  <Checkbox
                                    id="userFeedbackSettings.commentEnabled"
                                    label="Customer feedback"
                                    name="userFeedbackSettings.commentEnabled"
                                    checked={values.userFeedbackSettings.commentEnabled}
                                    onClickHandler={() => {
                                      setFieldValue('userFeedbackSettings.commentEnabled', !values.userFeedbackSettings.commentEnabled);
                                    }}
                                  />
                                </div>
                              </>
                            )
                          }
                        </div>
                        <div className="row">
                          {
                            hasPermission(EDIT_SMART_SCAN_USER_CONSENT_SETTINGS) && (
                              <div className="col-sm-4">
                                <Checkbox
                                  id="smartScanUserConsentEnabled"
                                  label="User consent enabled"
                                  name="smartScanUserConsentEnabled"
                                  checked={values.smartScanUserConsentEnabled}
                                  onClickHandler={() => {
                                    setFieldValue(
                                      'smartScanUserConsentEnabled',
                                      !values.smartScanUserConsentEnabled
                                    );
                                  }}
                                />
                              </div>
                            )
                          }
                          {
                            hasPermission(EDIT_SMART_SCAN_WINDSHIELD_REPAIR_SUMMARY_SETTINGS) && (
                              <div className="col-sm-4">
                                <Checkbox
                                  id="smartScanWindshieldRepairSummaryEnabled"
                                  label="Display repair decision"
                                  name="smartScanWindshieldRepairSummaryEnabled"
                                  checked={values.smartScanWindshieldRepairSummaryEnabled}
                                  onClickHandler={() => {
                                    setFieldValue(
                                      'smartScanWindshieldRepairSummaryEnabled',
                                      !values.smartScanWindshieldRepairSummaryEnabled
                                    );
                                  }}
                                />
                              </div>
                            )
                          }
                        </div>
                      </Card.Body>
                    </Card>
                  </div>
                </div>
                <div className="row">
                  <div className="col-10 col-sm-12 pt-3 text-right">
                    <button
                      type="button"
                      className="btn btn-secondary mr-1"
                      disabled={isSubmitting}
                      onClick={() => {
                        onHideHandler(hasUnsavedChanges, hideModal, setShowConfirmModal);
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={isSavingDisabled || isSubmitting}>
                      Save
                    </button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>
    </>
  );
};

WorkflowModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  workflow: PropTypes.object,
  isSavingDisabled: PropTypes.bool,
};

export default WorkflowModal;
